/**
 * @format
 * @flow strict-local
 */
// DEPENDENCIES

import React, {
	useEffect,
	useState
} from "react";
import {
	useNavigate,
	useSearchParams
} from "react-router-dom";

import {
	ComponentWrapper
} from "./SignIn.styled";

import Button from "../../components/Button";
import Card, {
	CardBody,
	CardFooter,
	CardHeader
} from "../../components/Card";
import InputText from "../../components/InputText";
import InputWrapper from "../../components/InputWrapper";

import routerHelper from "../../helpers/router";
import storeHelper from "../../helpers/store";
import utilsHelper from "../../helpers/utils";

import useApi from "../../hooks/api";
import useForm from "../../hooks/form";

import i18n from "../../i18n";

// COMPONENT

const SignIn = (props) => {
	const [
		isRenderReady, 
		setIsRenderReady
	] = useState(false);
	const [
		qs, 
		setQs
	] = useState(null);

	const {
		fetchApi
	} = useApi();
	const {
		eventHandler,
		getFormValues,
		getInputErrorText,
		hasError,
		initForm,
		inputConfigs,
		inputs,
		inputStates,
		updateInputConfigs,
		updateInputErrors,
		updateInputs,
		updateInputStates,
		validateForm
	} = useForm();
	const navigate = useNavigate();
	const [
		searchParams
	] = useSearchParams();

	// Lifecycle

	// Note - Run on bootstrap
	useEffect(
		() => {
			setQs(Object.fromEntries([
				...searchParams
			]));
	    }, 
		[]
	);

	// Note - Run when qs change
	useEffect(
		() => {
			if(!utilsHelper.isObject(qs)) {
				return;
			}

			initInputs();
	    }, 
		[qs]
	);

	// Note - Run when inputs change
	useEffect(
		() => {
			if(utilsHelper.isObjectEmpty(inputs)) {
				return;
			}

			if(!isRenderReady) {
				setIsRenderReady(true);
			}
		}, 
		[inputs]
	);

	// Events

	/**
	 * goToForgotPassword - ... pass qs
	 * 
	 * @param {object} e ... 
	 */
	const goToForgotPassword = (e) => {
		navigate({
			pathname: routerHelper.getUrl("forgotPassword")
		});
	};

	/**
	 * goToResendActivationCode - ... pass qs
	 * 
	 * @param {object} e ... 
	 */
	const goToResendActivationCode = (e) => {
		navigate({
			pathname: routerHelper.getUrl("resendActivationCode")
		});
	};

	/**
	 * goToSignUp - ... pass qs
	 * 
	 * @param {object} e ... 
	 */
	const goToSignUp = (e) => {
		navigate({
			pathname: routerHelper.getUrl("signUp")
		});
	};

	/**
	 * submitHandler - ...
	 * 
	 * @param {object} e ... 
	 */
	const submitHandler = async (e) => {
		if(
			!inputStates.isValid || 
			inputStates.isSubmit
		) {
			validateForm(false);

			return;
		}

		updateInputStates({
			isSubmit: true
		});

		const data = getFormValues();
		const response = await fetchApi({
			method: "get",
			params: {
				app_version: "4.0.0", // Note - Need to bypass native app checkup
				device_id: "1", // Note - Need to bypass native app checkup
				device_os: "browser", // Note - Need to bypass native app checkup
				service: "login",
				...data
			}
		});

		if(response.error) {
			updateInputStates({
				isSubmit: false,
				isValid: false
			});

			return;
		}

		storeHelper.dispatch( 
			"SET_USER_ACCESS_TOKEN", 
			response.payload.access_token
		);
		storeHelper.dispatch(
			"SET_USER_PROFILE", 
			{
				email: response.payload.email,
				firstName: response.payload.first_name,
				lastName: response.payload.last_name,
				phone: {
					countryCode: response.payload.phone_country_code,
					number: response.payload.phone
				}
			}
		);
		/*
		storeHelper.dispatch(
			"SET_USER_NOTIFICATIONS", 
			{
				//...
			}
		);
		storeHelper.dispatch(
			"SET_USER_SIZES", 
			{
				//...
			}
		);
		*/
		updateInputStates({
			isSubmit: false,
			isSubmitted: true
		});

		if(qs.redirect && qs.redirect !== "/") {
			navigate(qs.redirect);
		} else {
			navigate({
				pathname: routerHelper.getUrl("lists")
			});
		}
	};

	// Misc

	/**
	 * initInputs - ...
	 */
	const initInputs = () => {
		const newForm = {
			email: {
				formatter: "lowercase",
				initialValue: "",
				inputErrorType: "email",
				inputType: "text",
				touched: false,
				validators: {
					email: true,
					required: true
				}
			},
			password: {
				formatter: "",
				initialValue: "",
				inputErrorType: "password",
				inputType: "text",
				touched: false,
				validators: {
					required: true
				}
			}
		};

		initForm(newForm);
	};

	// Render

	/**
	 * render - ...
	 */
	const render = () => {
		if(!isRenderReady) {
			return null;
		}

		return (
			<ComponentWrapper>
				<Card
					className="card"
					>
					<CardHeader>
						<strong>
							Welcome to Copdate
						</strong>
						<p>
							Please enter your account details to sign in.
						</p>
					</CardHeader>
					<CardBody
						className="cardBody"
						>
						<InputWrapper
							label="Email"
							>
							<InputText
								name="email"
								onBlur={eventHandler}
								onChange={eventHandler}
								type="email"
								{...inputs.email}
							/>
						</InputWrapper>
						<InputWrapper
							label="Password"
							>
							<InputText
								name="password"
								onBlur={eventHandler}
								onChange={eventHandler}
								type="password"
								{...inputs.password}
							/>
						</InputWrapper>
						<a
							onClick={goToForgotPassword}
							>
							Forgot password?
						</a>
						<a
							onClick={goToResendActivationCode}
							>
							Resend activation code
						</a>
					</CardBody>
					<CardFooter>
						<Button
							className="footerButton"
							disabled={
								!isRenderReady ||
								inputStates.isSubmit ||
								!inputStates.isValid
							}
							label="Sign in"
							loaderActive={inputStates.isSubmit}
							loaderEnable
							onClick={submitHandler}
							paletteColor={inputStates.isValid ? "dark" : "disabled"}
							type="submit"
						/>
					</CardFooter>
				</Card>
				<span>
					Don't have an account?
					&nbsp;
					<a
						onClick={goToSignUp}
						>
						Sign up
					</a>
				</span>
			</ComponentWrapper>
		);
	};

	return render();
};

// EXPORT

export default SignIn;
